import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/de';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import PaymentModal from '../components/PaymentModal';
import RatingModal from '../components/RatingModal';
import './MeineAnfragen.scss';

moment.locale('de');

const STATUS_CONFIG = {
  offen:      { label: 'Ausstehend', icon: 'bi-hourglass-split',   color: '#d97706' },
  akzeptiert: { label: 'Akzeptiert', icon: 'bi-check-circle-fill', color: '#059669' },
  abgelehnt:  { label: 'Abgelehnt',  icon: 'bi-x-circle-fill',     color: '#dc2626' },
  storniert:  { label: 'Storniert',  icon: 'bi-slash-circle-fill',  color: '#6b7280' },
  geplant:    { label: 'Geplant',    icon: 'bi-calendar-event',    color: '#3b82f6' },
  passiert:   { label: 'Bezahlbar',  icon: 'bi-cash-coin',         color: '#b95c50' },
  abgeschlossen: { label: 'Bezahlt', icon: 'bi-check-all',         color: '#059669' },
  abgesagt:   { label: 'Abgesagt',   icon: 'bi-x-octagon',         color: '#6b7280' },
};

const JITSI_BASE = 'https://jitsi.maryu.eu/studyhelper/';
const ROOM_SALT = 's4lt!X9-studyhelper';

async function generateRoomId(terminId, lernEinheitId) {
  const raw = `studyhelper-${terminId}-${lernEinheitId}-${ROOM_SALT}`;
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hex = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20,32)}`;
}

function StatusStepper({ status }) {
  const steps = [
    { key: 'offen',      label: 'Angefragt'  },
    { key: 'akzeptiert', label: 'Bestätigt'  },
    { key: 'abgeschlossen', label: 'Bezahlt' },
  ];

  if (status === 'abgelehnt' || status === 'storniert' || status === 'abgesagt') {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.abgelehnt;
    return (
      <div className="status-cancelled">
        <i className={`bi ${cfg.icon} me-2`} style={{ color: cfg.color }} />
        <span style={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</span>
      </div>
    );
  }

  const activeIndex = (status === 'akzeptiert' || status === 'geplant' || status === 'passiert') ? 1 : (status === 'abgeschlossen' ? 2 : 0);

  return (
    <div className="status-stepper">
      {steps.map((step, idx) => (
        <React.Fragment key={step.key}>
          <div className={`step ${idx <= activeIndex ? 'active' : ''} ${idx < activeIndex ? 'done' : ''}`}>
            <div className="step-dot">{idx < activeIndex ? <i className="bi bi-check" /> : idx + 1}</div>
            <span className="step-label">{step.label}</span>
          </div>
          {idx < steps.length - 1 && <div className={`step-line ${idx < activeIndex ? 'done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function MeineAnfragen() {
  const { token, user } = useAuth();
  const [anfragen, setAnfragen] = useState([]);
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedTermin, setSelectedTermin] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedTerminForRating, setSelectedTerminForRating] = useState(null);
  const [localRatings, setLocalRatings] = useState({});
  const [roomIds, setRoomIds] = useState({});

  const isTutor = user?.role === 'dozent';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const [aRes, tRes] = await Promise.all([
        fetch(isTutor ? `${getBackendUrl()}/api/anfragen/tutor` : `${getBackendUrl()}/api/anfragen`, auth),
        fetch(`${getBackendUrl()}/termine`, auth)
      ]);
      
      if (!aRes.ok || !tRes.ok) throw new Error('Fehler beim Laden der Daten.');
      
      const aData = await aRes.json();
      const tData = await tRes.json();
      
      setAnfragen(aData.sort((a, b) => new Date(b.angefragtAm) - new Date(a.angefragtAm)));
      setTermine(tData.sort((a, b) => new Date(b.datumZeit) - new Date(a.datumZeit)));

      // Bewertungen für abgeschlossene/bezahlte Einheiten parallel abrufen
      if (!isTutor) {
        const completedTermine = tData.filter(t => t.status === 'abgeschlossen' || t.zahlungsstatus === 'bezahlt');
        const ratingsPromises = completedTermine.map(async (t) => {
          try {
            const rRes = await fetch(`${getBackendUrl()}/termine/${t.id}/bewertung`, auth);
            if (rRes.ok) {
              const rData = await rRes.json();
              if (rData && rData.length > 0) {
                return { terminId: t.id, rating: rData[0].bewertung, feedback: rData[0].kommentar };
              }
            }
          } catch (err) {
            console.error(`Fehler beim Laden der Bewertung für Termin ${t.id}:`, err);
          }
          return null;
        });

        const ratingsResults = await Promise.all(ratingsPromises);
        const ratingsMap = {};
        ratingsResults.forEach(r => {
          if (r) {
            ratingsMap[r.terminId] = { rating: r.rating, feedback: r.feedback };
          }
        });
        setLocalRatings(ratingsMap);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, isTutor]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Generate deterministic Jitsi room IDs for active termine
  useEffect(() => {
    const activeTermine = termine.filter(t =>
      ['geplant', 'akzeptiert', 'passiert'].includes(t.status)
    );
    if (activeTermine.length === 0) return;
    Promise.all(
      activeTermine.map(async t => ({
        id: t.id,
        room: await generateRoomId(t.id, t.lernEinheitId || t.id)
      }))
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.id] = r.room; });
      setRoomIds(prev => ({ ...prev, ...map }));
    });
  }, [termine]);

  const handlePayConfirm = async (id) => {
    const res = await fetch(`${getBackendUrl()}/termine/${id}/bezahlen`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Zahlung vom Server abgelehnt.');
    fetchData(); // Reload all
  };

  const handleStornieren = async (id) => {
    if (!window.confirm('Wirklich stornieren?')) return;
    setActionId(id);
    try {
      const res = await fetch(`${getBackendUrl()}/api/anfragen/${id}/stornieren`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Stornierung fehlgeschlagen.');
      fetchData();
    } catch (err) { setError(err.message); } finally { setActionId(null); }
  };

  const handleBearbeiten = async (id, akzeptieren) => {
    const action = akzeptieren ? 'akzeptieren' : 'ablehnen';
    setActionId(id);
    try {
      const res = await fetch(`${getBackendUrl()}/api/anfragen/${id}/${action}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Aktion fehlgeschlagen.');
      fetchData();
    } catch (err) { setError(err.message); } finally { setActionId(null); }
  };

  const handleRateConfirm = async (terminId, rating, feedback) => {
    setError('');
    try {
      const res = await fetch(`${getBackendUrl()}/termine/${terminId}/bewertung`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          bewertung: rating,
          kommentar: feedback
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Fehler beim Speichern der Bewertung.');
      }

      setLocalRatings(prev => ({
        ...prev,
        [terminId]: { rating, feedback }
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownloadRechnung = async (id) => {
    setError('');
    try {
      const response = await fetch(`${getBackendUrl()}/rechnung/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorMsg = await response.text().catch(() => '');
        throw new Error(errorMsg || 'Fehler beim Laden der Rechnung. Bitte stellen Sie sicher, dass Sie und Ihr Dozent/Schüler eine gültige Adresse hinterlegt haben.');
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Rechnung_Buchung_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Fehler beim Herunterladen der Rechnung:", err);
      setError(err.message || 'Die Rechnung konnte nicht heruntergeladen werden.');
    }
  };

  const filters = [
    { key: 'alle',      label: 'Alle' },
    { key: 'offen',     label: 'Ausstehend' },
    { key: 'termine',   label: 'Unterricht' },
    { key: 'abgeschlossen', label: 'Bezahlt' },
  ];

  const filteredItems = useMemo(() => {
    try {
      // Create a map/list of consolidated items based on termine (actual lessons)
      const consolidatedTermine = termine.map(t => {
        // Find matching inquiry using the high-precision angefragtAm timestamp
        const matchingAnfrage = anfragen.find(a => 
          a.angefragtAm && t.angefragtAm && 
          moment(a.angefragtAm).isSame(moment(t.angefragtAm))
        );

        const consolidated = {
          ...t,
          anfrageId: matchingAnfrage ? matchingAnfrage.id : null,
        };

        if (matchingAnfrage) {
          // Resolve correct status and properties dynamically
          if (matchingAnfrage.status === 'offen') {
            consolidated.status = 'offen'; // Overwrite planned status with pending status
            consolidated.istBezahlbar = false; // Cannot pay pending bookings
          } else if (matchingAnfrage.status === 'abgelehnt') {
            consolidated.status = 'abgelehnt';
            consolidated.istBezahlbar = false;
          } else if (matchingAnfrage.status === 'storniert') {
            consolidated.status = 'storniert';
            consolidated.istBezahlbar = false;
          }
        }

        return consolidated;
      });

      // Find any inquiries that do NOT have an associated termin (orphaned or corner cases)
      const orphanedAnfragen = anfragen.filter(a => 
        !termine.some(t => t.angefragtAm && a.angefragtAm && moment(t.angefragtAm).isSame(moment(a.angefragtAm)))
      );

      const orphanedMapped = orphanedAnfragen.map(a => ({
        id: a.id,
        anfrageId: a.id,
        tutor: a.tutor,
        schueler: user, // fallback
        datumZeit: a.datumZeit,
        dauerMinuten: a.dauerMinuten,
        skillName: a.fach?.name,
        beschreibung: a.beschreibung,
        status: a.status,
        angefragtAm: a.angefragtAm,
        preis: a.preis,
        zahlungsstatus: 'offen',
        istBezahlbar: false
      }));

      // Combine consolidated lessons and orphaned inquiries
      const combined = [...consolidatedTermine, ...orphanedMapped];

      // Sort combined list by date (most recent first)
      const sorted = combined.sort((a, b) => new Date(b.datumZeit || b.angefragtAm) - new Date(a.datumZeit || a.angefragtAm));

      if (filter === 'alle') {
        return sorted;
      }
      if (filter === 'offen') {
        return sorted.filter(item => item.status === 'offen');
      }
      if (filter === 'termine') {
        return sorted.filter(item => ['geplant', 'passiert', 'akzeptiert'].includes(item.status));
      }
      if (filter === 'abgeschlossen') {
        return sorted.filter(item => item.status === 'abgeschlossen' || item.zahlungsstatus === 'bezahlt');
      }
      
      return sorted.filter(item => item.status === filter);
    } catch (e) {
      console.error("Filter error:", e);
      return [];
    }
  }, [filter, anfragen, termine, user]);

  if (!user) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

  return (
    <div className="meine-anfragen">
      <div className="anfragen-header">
        <h1 className="page-title">Meine Buchungen & Zahlungen</h1>
        <p className="page-subtitle">Verwalte deine Lernreise und anstehende Abrechnungen.</p>
      </div>

      <Container className="anfragen-body">
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error} <Button variant="link" onClick={fetchData} className="p-0 ms-2">Erneut versuchen</Button></Alert>}

        <div className="filter-tabs mb-4">
          {filters.map(f => (
            <button key={f.key} className={`filter-tab ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-state"><Spinner animation="border" /><p>Lade Daten...</p></div>
        ) : filteredItems.length === 0 ? (
          <div className="empty-state"><h4>Keine Einträge gefunden</h4><p className="text-muted">In diesem Bereich gibt es aktuell nichts zu tun.</p></div>
        ) : (
          <div className="anfragen-list">
            {filteredItems.map(item => {
              const isTermin = item.hasOwnProperty('zahlungsstatus');
              const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.offen;
              const date = item.datumZeit || item.angefragtAm;

              return (
                <div key={(isTermin ? 't-' : 'a-') + item.id} className={`anfrage-card status-${item.status}`}>
                  <div className="card-accent" style={{ '--accent': cfg.color }} />
                  <div className="card-inner">
                    <div className="card-date">
                      <div className="date-day">{moment(date).format('DD')}</div>
                      <div className="date-group">
                        <div className="date-weekday">{moment(date).format('ddd')}</div>
                        <div className="date-month">{moment(date).format('MMM')}</div>
                      </div>
                      <div className="date-time">{moment(date).format('HH:mm')}</div>
                    </div>

                    <div className="card-content">
                      <div className="card-top">
                        <h5 className="card-fach">{item.skillName || item.fach?.name || 'Nachhilfe'}</h5>
                        <StatusStepper status={item.status} />
                      </div>

                      <div className="card-meta-row">
                        <span className="meta-chip"><i className="bi bi-person-fill" /> {isTutor ? (item.schueler?.vollerName || 'Student') : (item.tutor?.vollerName || 'Dozent')}</span>
                        {item.dauerMinuten && <span className="meta-chip"><i className="bi bi-clock" /> {item.dauerMinuten} Min.</span>}
                        {item.preis != null && <span className="meta-chip"><i className="bi bi-tag" /> {Number(item.preis).toFixed(2)} €</span>}
                        {(item.zahlungsstatus === 'bezahlt' || item.status === 'abgeschlossen') && <Badge bg="success" className="ms-auto shadow-sm">Bezahlt</Badge>}
                      </div>

                      <div className="card-footer-row d-flex justify-content-between align-items-center mt-3">
                        <span className="card-angefragt small text-muted">
                          {isTermin ? 'Termin am ' + moment(item.datumZeit).format('DD.MM.YYYY') : 'Angefragt ' + moment(item.angefragtAm).fromNow()}
                        </span>
                        
                        <div className="action-buttons-wrap">
                          {item.status === 'offen' && !isTutor && (
                            <Button className="btn-stornieren" disabled={actionId === item.anfrageId} onClick={() => handleStornieren(item.anfrageId)}>Stornieren</Button>
                          )}
                          {item.status === 'offen' && isTutor && (
                            <div className="d-flex gap-2">
                              <Button className="btn-akzeptieren" onClick={() => handleBearbeiten(item.anfrageId, true)}>Annehmen</Button>
                              <Button className="btn-ablehnen" onClick={() => handleBearbeiten(item.anfrageId, false)}>Ablehnen</Button>
                            </div>
                          )}
                          {item.istBezahlbar && !isTutor && user?.id === item.schueler?.id && (
                            <Button 
                              className="btn-pay-now"
                              onClick={() => { setSelectedTermin(item); setShowPayModal(true); }}
                            >
                              <i className="bi bi-credit-card me-2"></i> Jetzt Bezahlen
                            </Button>
                          )}
                          {['geplant', 'akzeptiert', 'passiert'].includes(item.status) && roomIds[item.id] && (
                            <Button
                              className="btn-videocall"
                              onClick={() => window.open(`${JITSI_BASE}${roomIds[item.id]}`, '_blank')}
                            >
                              <i className="bi bi-camera-video-fill me-2"></i>Videocall beitreten
                            </Button>
                          )}
                          {item.status === 'abgeschlossen' && !isTutor && user?.id === item.schueler?.id && (
                            <Button 
                              className="btn-invoice"
                              onClick={() => handleDownloadRechnung(item.id)}
                            >
                              <i className="bi bi-file-earmark-pdf me-2"></i> Rechnung
                            </Button>
                          )}
                          {item.status === 'abgeschlossen' && !isTutor && user?.id === item.schueler?.id && (
                            <React.Fragment>
                              {localRatings[item.id] ? (
                                <div className="rating-display-badge d-flex align-items-center px-3 py-1 rounded-pill">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <i 
                                      key={star} 
                                      className={`bi ${star <= localRatings[item.id].rating ? 'bi-star-fill' : 'bi-star'} text-warning me-1`} 
                                      style={{ fontSize: '0.9rem' }} 
                                    />
                                  ))}
                                  <span className="ms-2 small fw-bold text-success">Bewertet</span>
                                </div>
                              ) : (
                                <Button 
                                  className="btn-rate-now"
                                  onClick={() => { setSelectedTerminForRating(item); setShowRatingModal(true); }}
                                >
                                  <i className="bi bi-star-fill me-2"></i> Bewerten
                                </Button>
                              )}
                            </React.Fragment>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Container>
      
      {selectedTermin && (
        <PaymentModal 
          show={showPayModal} 
          onHide={() => setShowPayModal(false)} 
          termin={selectedTermin} 
          onConfirm={handlePayConfirm}
        />
      )}
      
      {selectedTerminForRating && (
        <RatingModal 
          show={showRatingModal} 
          onHide={() => setShowRatingModal(false)} 
          termin={selectedTerminForRating} 
          onConfirm={handleRateConfirm}
        />
      )}
    </div>
  );
}
